import { useTranslation } from "react-i18next";
import { Circle } from "lucide-react";
import { type ScrapedUser } from "@/lib/scraper";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";

/**
 * LastOnline — renders the TG-side "last seen" signal with recency coloring.
 *
 * Shared by the Audience table cell and the user DetailDrawer so both show the
 * exact same phrasing (relative time + tooltip for the precise `offline` case,
 * localized bucket for the privacy-hidden cases).
 */
export function LastOnline({ user }: { user: ScrapedUser }) {
  const { t } = useTranslation();
  const kind = user.last_online_kind;

  const colorByKind: Record<string, string> = {
    online: "text-success",
    offline: "text-fg-primary",
    recently: "text-fg-primary",
    last_week: "text-fg-secondary",
    last_month: "text-fg-muted",
    long_ago: "text-danger",
    hidden: "text-fg-muted",
  };
  const color = (kind && colorByKind[kind]) ?? "text-fg-muted";

  if (kind === "offline" && user.last_seen_at) {
    const ts = new Date(user.last_seen_at);
    return (
      <span className={color} title={formatDateTime(ts)}>
        {relativeTime(ts, t)}
      </span>
    );
  }

  if (!kind) {
    return <span className="text-fg-muted">—</span>;
  }
  if (kind === "online") {
    return (
      <span className={cn("inline-flex items-center gap-1.5", color)}>
        <Circle size={8} className="fill-current" aria-hidden="true" />
        {t("users.last_seen.online")}
      </span>
    );
  }
  // A bucket we have no label for must not leak the i18n key itself into the
  // table ("users.last_seen.within_month" was showing up verbatim).
  return (
    <span className={cn(color)}>
      {t(`users.last_seen.${kind}`, { defaultValue: t("users.last_seen.unknown") })}
    </span>
  );
}

function relativeTime(
  ts: Date,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  const diffMs = Date.now() - ts.getTime();
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (sec < 60) return t("users.last_seen.just_now");
  if (min < 60) return t("users.last_seen.minutes_ago", { count: min });
  if (hr < 24) return t("users.last_seen.hours_ago", { count: hr });
  if (day < 30) return t("users.last_seen.days_ago", { count: day });
  if (day < 365)
    return t("users.last_seen.months_ago", { count: Math.floor(day / 30) });
  return t("users.last_seen.years_ago", { count: Math.floor(day / 365) });
}
